import { MarkdownEditor } from "@octopusdeploy/design-system-components";
import { TextWithLabel as Text } from "@octopusdeploy/design-system-octopus-components";
import type { CreateDeploymentFreezeCommand, ScopeToEnvironmentMapping, TenantProjectEnvironment, RecurringScheduleResource, DeploymentFreezeDetailEnvironment, DeploymentFreezeDetailStatus, DeploymentFreezeScopeDetail, DeploymentFreezeTenantScopeDetail, GetDeploymentFreezeDetailBffResponse, ModifyDeploymentFreezeCommand, ProjectResource, } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import type { Moment } from "moment";
import moment from "moment";
import pluralize from "pluralize";
import React from "react";
import rollForward from "~/areas/projects/components/Releases/Deployments/NowOrLater/rollFoward";
import { repository } from "~/clientInstance";
import { OptionalFormBaseComponent } from "~/components/FormBaseComponent/FormBaseComponent";
import type { FormBaseComponentState } from "~/components/FormBaseComponent/FormBaseComponent";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import InternalRedirect from "~/components/Navigation/InternalRedirect";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenuConverterVNext } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import type { PageContentProps } from "~/components/PageContent/PageContent";
import { PageContent } from "~/components/PageContent/PageContent";
import { ExpandableFormSection, ExpansionButtons, Summary, required } from "~/components/form/index";
import { isLicensedFeatureAvailable } from "../LicensedFeature/LicensedFeatureContext";
import { FreezeProjectScopeSection } from "./FreezeProjectScopeSection";
import { FreezeScheduleSection } from "./FreezeScheduleSection";
import { FreezeTenantScopeSection } from "./FreezeTenantScopeSection";
interface CreateDeploymentFreezeProps {
    create: true;
    deploymentFreezeId?: string;
    project?: ProjectResource;
}
interface EditDeploymentFreezeProps {
    create: false;
    deploymentFreezeId: string;
    project?: ProjectResource;
}
type DeploymentFreezeProps = CreateDeploymentFreezeProps | EditDeploymentFreezeProps;
export const CreateOrEditDeploymentFreeze = (props: DeploymentFreezeProps) => {
    return <CreateOrEditDeploymentFreezeInternalPage {...props}/>;
};
interface CreateOrEditDeploymentFreezeState extends FormBaseComponentState<DeploymentFreezeModel> {
    isLoaded: boolean;
    hasError: boolean;
    redirectTo: LinkHref;
    currentPageIndex: number;
    focussedScopeCellId: string;
    disableSaveButton: boolean;
}
export type DeploymentFreezeModel = FreezeDisplayProps;
interface FreezeDisplayProps {
    id: string;
    name: string;
    description: string;
    startDate: Moment;
    endDate: Moment;
    status: DeploymentFreezeDetailStatus;
    userTimezone: string;
    userUtcOffset: number;
    projectScope: DeploymentFreezeScopeDetail[];
    recurringSchedule?: RecurringScheduleResource;
    tenantScopes: DeploymentFreezeTenantScopeDetail[];
}
const currentTime = new Date();
const scheduledTime = rollForward(moment(currentTime), 10);
const expiryTime = rollForward(scheduledTime, 30);
export type CreateOrEditDeploymentFreezeInternalProps = DeploymentFreezeProps;
class CreateOrEditDeploymentFreezeInternalPage extends OptionalFormBaseComponent<CreateOrEditDeploymentFreezeInternalProps, CreateOrEditDeploymentFreezeState, DeploymentFreezeModel> {
    private readonly defaultFreezeModel: DeploymentFreezeModel = {
        id: "",
        name: "",
        description: "",
        startDate: scheduledTime,
        endDate: expiryTime,
        status: undefined,
        userTimezone: moment(currentTime).format("[GMT] Z"),
        userUtcOffset: moment(currentTime).utcOffset(),
        projectScope: [],
        tenantScopes: [],
        recurringSchedule: undefined,
    };
    constructor(props: CreateOrEditDeploymentFreezeInternalProps) {
        super(props);
        this.state = {
            hasError: false,
            isLoaded: false,
            model: this.defaultFreezeModel,
            cleanModel: this.defaultFreezeModel,
            redirectTo: "",
            currentPageIndex: 0,
            focussedScopeCellId: "",
            disableSaveButton: false,
        };
    }
    async componentDidMount() {
        await this.doBusyTask(async () => {
            let model = this.defaultFreezeModel;
            if (this.props.deploymentFreezeId) {
                const response = await repository.DeploymentFreezes.getDetailBff(this.props.deploymentFreezeId, this.props.project?.Id);
                model = this.buildModel(response);
            }
            const globalDeploymentFreezeAvailable = await isLicensedFeatureAvailable("GlobalDeploymentFreeze");
            const disableSaveButton = this.props.create && !this.props.project && !globalDeploymentFreezeAvailable;
            this.setState({
                isLoaded: true,
                model,
                cleanModel: model,
                disableSaveButton,
            });
        }, {
            onError: (error) => {
                this.setState({
                    isLoaded: true,
                    hasError: true,
                });
            },
            onSuccess: () => { },
        });
    }
    genericValidationMessage = "The deployment freeze could not be saved due to the following errors.";
    isValid = (projectScope: DeploymentFreezeScopeDetail[], tenantScopes: DeploymentFreezeTenantScopeDetail[]): boolean => {
        const isUntenantedProject = this.props.project?.TenantedDeploymentMode === "Untenanted";
        const isGlobalFreeze = !this.props.project;
        const projectsWithoutEnvironments: string[] = projectScope.filter((s) => s.Environments.length == 0).map((s) => s.Name);
        const projectEnvironmentScopes: ScopeToEnvironmentMapping = {};
        const tenantsWithoutEnvironments: string[] = [];
        const tenantedProjectFreeze = !isUntenantedProject && !isGlobalFreeze;
        projectScope.forEach((detail) => {
            projectEnvironmentScopes[detail.Id] = detail.Environments.map((e) => e.Id);
        });
        tenantScopes.forEach((detail) => {
            if (detail.ProjectEnvironments.length === 0 || detail.ProjectEnvironments.some((pe) => pe.Environments.length == 0)) {
                tenantsWithoutEnvironments.push(detail.Name);
            }
        });
        // Global freeze need at least 1 project or tenant.
        if (isGlobalFreeze && Object.keys(projectEnvironmentScopes).length === 0 && tenantScopes.length === 0) {
            this.setValidationErrors(this.genericValidationMessage, { Scope: "Please assign at least 1 project or tenant to this freeze" });
            return false;
        }
        // Project freeze need at least 1 environment or tenant.
        if (!isGlobalFreeze && projectsWithoutEnvironments.length === 1 && tenantScopes.length === 0) {
            this.setValidationErrors(this.genericValidationMessage, { Scope: `Please assign ${isUntenantedProject ? "" : "at least 1 tenant or assign"} at least 1 environment to this freeze` });
            return false;
        }
        // Valid tenant scope need to have project and environments
        if (tenantsWithoutEnvironments.length > 0) {
            const tenants = tenantsWithoutEnvironments.length < 20 ? tenantsWithoutEnvironments.join(", ") : `${tenantsWithoutEnvironments.slice(0, 19).join(", ")} and ${tenantsWithoutEnvironments.length - 20} more`;
            this.setValidationErrors(this.genericValidationMessage, { Scope: `Please select at least 1 environment for each project in the following ${pluralize("tenant", tenantsWithoutEnvironments.length)}: ${tenants}` });
            return false;
        }
        // Valid project scope need to have environments
        if (!tenantedProjectFreeze && projectsWithoutEnvironments.length > 0) {
            const projects = projectsWithoutEnvironments.length < 20 ? projectsWithoutEnvironments.join(", ") : `${projectsWithoutEnvironments.slice(0, 19).join(", ")} and ${projectsWithoutEnvironments.length - 20} more`;
            this.setValidationErrors(this.genericValidationMessage, {
                Scope: isGlobalFreeze ? `Please select at least 1 environment for the following ${pluralize("project", projectsWithoutEnvironments.length)}: ${projects}` : `Please select at least 1 environment to this freeze`,
            });
            return false;
        }
        return true;
    };
    handleSaveClick = async () => {
        await this.doBusyTask(async () => {
            const { id, name, startDate, endDate, projectScope, tenantScopes, recurringSchedule, description } = this.state.model;
            const projectEnvironmentScopes: ScopeToEnvironmentMapping = {};
            let tenantEnvironmentScopes: TenantProjectEnvironment[] = [];
            if (!this.isValid(projectScope, tenantScopes)) {
                return;
            }
            projectScope
                .filter((s) => s.Environments.length > 0)
                .forEach((detail) => {
                projectEnvironmentScopes[detail.Id] = detail.Environments.map((e) => e.Id);
            });
            tenantScopes.forEach((detail) => {
                tenantEnvironmentScopes = [...tenantEnvironmentScopes, ...detail.ProjectEnvironments.flatMap((pe) => pe.Environments.map((e) => ({ TenantId: detail.Id, ProjectId: pe.Id, EnvironmentId: e.Id })))];
            });
            if (this.props.create) {
                const createCommand: CreateDeploymentFreezeCommand = {
                    Id: id,
                    Name: name,
                    Start: startDate,
                    End: endDate,
                    ProjectEnvironmentScope: projectEnvironmentScopes,
                    TenantProjectEnvironmentScope: tenantEnvironmentScopes,
                    RecurringSchedule: recurringSchedule,
                    OwnerId: this.props.project ? this.props.project.Id : undefined,
                    Description: description,
                };
                const response = await repository.DeploymentFreezes.create(createCommand);
                this.setState({
                    model: this.defaultFreezeModel,
                    cleanModel: this.defaultFreezeModel,
                    redirectTo: !this.props.project
                        ? links.editDeploymentFreezePage.generateUrl({ deploymentFreezeId: response.Id })
                        : links.projectDeploymentFreezePage.generateUrl({ spaceId: this.props.project?.SpaceId, projectSlug: this.props.project?.Slug, deploymentFreezeId: response.Id }),
                });
            }
            else {
                const modifyCommand: ModifyDeploymentFreezeCommand = {
                    Id: id,
                    Name: name,
                    Start: startDate,
                    End: endDate,
                    ProjectEnvironmentScope: projectEnvironmentScopes,
                    TenantProjectEnvironmentScope: tenantEnvironmentScopes,
                    RecurringSchedule: recurringSchedule,
                    OwnerId: this.props.project ? this.props.project.Id : undefined,
                    Description: description,
                };
                const response = await repository.DeploymentFreezes.modify(modifyCommand);
                const updatedFreeze = await repository.DeploymentFreezes.getDetailBff(response.Id, this.props.project?.Id);
                this.setState({
                    model: this.buildModel(updatedFreeze),
                    cleanModel: this.buildModel(updatedFreeze),
                });
            }
        });
    };
    handleDeploymentFreezeDelete = async () => {
        await this.doBusyTask(async () => {
            await repository.DeploymentFreezes.delete(this.state.model.id);
            this.setState({
                redirectTo: !this.props.project ? links.deploymentFreezesPage.generateUrl() : links.projectDeploymentFreezesPage.generateUrl({ spaceId: this.props.project?.SpaceId, projectSlug: this.props.project?.Slug }),
            });
        });
        return true;
    };
    buildModel(response: GetDeploymentFreezeDetailBffResponse): DeploymentFreezeModel {
        const model = {
            id: response.Id,
            name: response.Name,
            description: response.Description ?? "",
            startDate: moment(response.Start),
            endDate: moment(response.End),
            status: response.Status,
            userTimezone: moment(currentTime).format("[GMT] Z"),
            userUtcOffset: moment(currentTime).utcOffset(),
            projectScope: response.Project,
            tenantScopes: response.TenantScopes,
            recurringSchedule: response.RecurringSchedule,
        };
        return model;
    }
    onConnected = (model: DeploymentFreezeModel) => {
        this.setState({ model });
    };
    onFocusedScopeCell = (id: string) => {
        this.setState({ focussedScopeCellId: id });
    };
    onScopeCellLostFocus = () => {
        this.setState({ focussedScopeCellId: "" });
    };
    onProjectRemove = (project: DeploymentFreezeScopeDetail) => {
        const updatedScope = this.state.model.projectScope.filter((scope) => scope.Id !== project.Id);
        this.setState({ model: { ...this.state.model, projectScope: updatedScope } });
    };
    onTenantRemove = (projectEnvironmentScope: DeploymentFreezeScopeDetail, tenantId: string) => {
        const tenant = this.state.model.tenantScopes.find((scope) => scope.Id === tenantId);
        if (tenant) {
            const updatedTenant = {
                ...tenant,
                ProjectEnvironments: tenant.ProjectEnvironments.filter((scope) => scope.Id !== projectEnvironmentScope.Id),
            };
            const updatedTenantScopes = this.state.model.tenantScopes.map((scope) => (scope.Id === tenantId ? updatedTenant : scope)).filter((t) => t.ProjectEnvironments.length !== 0);
            this.setState({
                model: {
                    ...this.state.model,
                    tenantScopes: updatedTenantScopes,
                },
            });
        }
    };
    onProjectEnvironmentUpdate = (project: DeploymentFreezeScopeDetail, selectedEnvironments: DeploymentFreezeDetailEnvironment[], tenantId?: string) => {
        const updatedScope = this.state.model.projectScope.map((scope) => {
            if (scope.Id === project.Id) {
                return { ...scope, Environments: selectedEnvironments };
            }
            return scope;
        });
        this.setState({ model: { ...this.state.model, projectScope: updatedScope } });
        return Promise.resolve(true);
    };
    onTenantEnvironmentUpdate = (projectScope: DeploymentFreezeScopeDetail, selectedEnvironments: DeploymentFreezeDetailEnvironment[], tenantId?: string) => {
        const updatedScope = this.state.model.tenantScopes.map((scope) => {
            if (scope.Id === tenantId) {
                const projectEnvironments = scope.ProjectEnvironments.map((p) => {
                    if (p.Id == projectScope.Id) {
                        return { ...p, Environments: selectedEnvironments };
                    }
                    return p;
                });
                return { ...scope, ProjectEnvironments: projectEnvironments };
            }
            return scope;
        });
        this.setState({ model: { ...this.state.model, tenantScopes: updatedScope } });
        return Promise.resolve(true);
    };
    getCalloutConfiguration(): PageContentProps["callout"] | undefined {
        const { startDate, endDate, status, projectScope, tenantScopes, recurringSchedule } = this.state.model;
        const currentStartDate = moment(recurringSchedule?.StartDate || startDate);
        const currentEndDate = moment(recurringSchedule?.EndDate || endDate);
        const hasTenants = tenantScopes.length > 0;
        const hasProjects = projectScope.length > 0;
        const getScopeText = () => {
            if (this.props.project) {
                if (!hasTenants) {
                    return "environments";
                }
                return "tenants and environments";
            }
            if (hasProjects && !hasTenants) {
                return "projects and environments";
            }
            return "projects, tenants and environments";
        };
        switch (status) {
            case "Active":
                return {
                    title: `This deployment freeze is currently active ${recurringSchedule ? "within a recurring schedule" : ""}`,
                    type: "success",
                    content: `Deployments to scoped ${getScopeText()} are frozen until ${currentEndDate.format("LT")} on ${currentEndDate.format("Do MMMM YYYY [GMT] Z")}`,
                };
            case "Scheduled":
                return {
                    title: "This deployment freeze is scheduled",
                    type: "information",
                    content: `Deployments to the scoped ${getScopeText()} will be frozen from ${currentStartDate.format("LT")} on ${currentStartDate.format("Do MMMM YYYY [GMT] Z")}`,
                };
            case "Expired":
                return {
                    title: "This deployment freeze has expired",
                    type: "warning",
                    content: `Deployments to the scoped ${getScopeText()} resumed at ${currentEndDate.format("LT")} on ${currentEndDate.format("Do MMMM YYYY [GMT] Z")}`,
                };
            default:
                return undefined;
        }
    }
    getOverflowActions() {
        const overflowActions = [];
        if (!this.props.create) {
            overflowActions.push(OverflowMenuItems.deleteItem("Remove", `Are you sure you want to delete '${this.state.model.name}'?`, () => this.handleDeploymentFreezeDelete(), <div>
                        <p>Do you wish to continue?</p>
                    </div>, { permission: this.props.project ? Permission.ProjectEdit : Permission.DeploymentFreezeAdminister, project: this.props.project?.Id }));
        }
        return overflowActions;
    }
    buildNameSummary = () => (this.state.model.name.length > 0 ? Summary.summary(this.state.model.name) : Summary.placeholder("No deployment freeze name provided"));
    buildDescriptionSummary = () => (this.state.model.description.length > 0 ? Summary.summary(this.state.model.description) : Summary.placeholder("No deployment freeze description provided"));
    buildProjectScopeSummary = () => (this.state.model.projectScope.length > 0 ? `${pluralize("project", this.state.model.projectScope.length, true)} assigned` : "Assign projects to this deployment freeze.");
    buildTenantScopeSummary = () => (this.state.model.tenantScopes.length > 0 ? `${pluralize("tenant", this.state.model.tenantScopes.length, true)} assigned` : "Assign tenants to this deployment freeze.");
    getTitle = () => (this.props.create ? "Create Deployment Freeze" : this.state.model.name);
    render() {
        if (this.state.redirectTo) {
            return <InternalRedirect to={this.state.redirectTo} push={true}/>;
        }
        const legacyOverflowActions = this.getOverflowActions();
        const overflowMenu = OverflowMenuConverterVNext.convertAll(legacyOverflowActions);
        return (<React.Fragment>
                {this.state.isLoaded && (<LegacyForm model={this.state.model} cleanModel={this.state.cleanModel} savePermission={{ permission: this.props.project ? Permission.ProjectEdit : Permission.DeploymentFreezeAdminister, project: this.props.project?.Id }} forceDisableFormSaveButton={this.state.disableSaveButton} onSaveClick={this.handleSaveClick}>
                        {({ FormContent, createSaveAction }) => (<PageContent header={{
                        title: this.getTitle(),
                        breadcrumbs: [
                            {
                                label: "Deployment Freezes",
                                pageUrl: !this.props.project ? links.deploymentFreezesPage.generateUrl() : links.projectDeploymentFreezesPage.generateUrl({ spaceId: this.props.project?.SpaceId, projectSlug: this.props.project?.Slug }),
                            },
                        ],
                        primaryAction: createSaveAction({}),
                        overflowActions: overflowMenu.menuItems,
                    }} busy={this.state.busy} errors={this.errors} callout={this.getCalloutConfiguration()}>
                                {overflowMenu.dialogs}
                                {!this.state.hasError && (<FormContent>
                                        <ExpansionButtons containerKey="FreezeEdit" errors={this.errors?.fieldErrors} expandAllOnMount={this.props.create}/>
                                        <ExpandableFormSection key="name" errorKey="name" title="Name" summary={this.buildNameSummary()} help={helpText.name.help} containerKey="FreezeEdit" isExpandedByDefault={true}>
                                            <Text value={this.state.model.name} label="Deployment Freeze Name" validate={required("Please provide a name for this deployment freeze")} onChange={(value) => {
                            this.setState({ model: { ...this.state.model, name: value } });
                        }}/>
                                        </ExpandableFormSection>
                                        <ExpandableFormSection key="description" errorKey="description" title="Description" summary={this.buildDescriptionSummary()} help={helpText.description.help} containerKey="FreezeEdit" isExpandedByDefault={true}>
                                            <MarkdownEditor value={this.state.model.description} label="Freeze description" onChange={(value) => this.setState({ model: { ...this.state.model, description: value } })}/>
                                        </ExpandableFormSection>
                                        <FreezeScheduleSection startDate={this.state.model.startDate} endDate={this.state.model.endDate} recurringSchedule={this.state.model.recurringSchedule} userTimezone={this.state.model.userTimezone} userUtcOffset={this.state.model.userUtcOffset} onScheduleUpdate={(startDate, endDate) => this.setChildState1("model", { startDate, endDate })} onRecurringScheduleUpdate={(schedule) => this.setChildState1("model", { recurringSchedule: schedule })}/>
                                        <FreezeProjectScopeSection model={this.state.model} project={this.props.project} deploymentFreezeId={this.props.deploymentFreezeId} focussedScopeCellId={this.state.focussedScopeCellId} onConnected={this.onConnected} onProjectEnvironmentUpdate={this.onProjectEnvironmentUpdate} onProjectRemove={this.onProjectRemove} onFocusedScopeCell={this.onFocusedScopeCell} onScopeCellLostFocus={this.onScopeCellLostFocus} doBusyTask={this.doBusyTask}/>
                                        {this.props.project?.TenantedDeploymentMode !== "Untenanted" && (<FreezeTenantScopeSection model={this.state.model} project={this.props.project} focussedScopeCellId={this.state.focussedScopeCellId} onConnected={this.onConnected} onTenantEnvironmentUpdate={this.onTenantEnvironmentUpdate} onTenantRemove={this.onTenantRemove} onFocusedScopeCell={this.onFocusedScopeCell} onScopeCellLostFocus={this.onScopeCellLostFocus} doBusyTask={this.doBusyTask}/>)}
                                    </FormContent>)}
                            </PageContent>)}
                    </LegacyForm>)}
            </React.Fragment>);
    }
}
const helpText = {
    name: {
        help: "Provide a name for this deployment freeze.",
    },
    description: {
        help: "Provide a description for this deployment freeze.",
    },
};
